import { Box, IconButton, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { FC, useContext, useState } from 'react';
import RecipeItemRow from './RecipeItemRow';
import { Recipe } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../../components/shared/Pill';
import { DotsVertical } from '../../../../assets';
import { Loader } from '../../../../components/ui/Loader';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { exportFile } from '../../../../utils';
import useLogError from '../../../../hooks/useLogError';
import { TableLoading } from '../../../../components/forms/TableLoading';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../../components/shared/EmptyTable';

type Props = {
  handleOpenBulk: () => void;
  list: Recipe[];
  currencyCode?: string;
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
}

const RecipeList: FC<Props> = ({ list, currencyCode, isLoading, totalCount, onClear, onCreateNew, handleOpenBulk }) => {
  const { t } = useTranslation('recipe');
  const { selectedStore, user, hasPermissionTo } = useContext(UserContext);
  const { exportRecipes } = useNswagClient();
  const { logError } = useLogError();

  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const shouldShowBulkManage = (): boolean => {
    const allowedFranchises = ['NE Apple Green GB', 'Pasta Evangelists UK', 'Insomnia Cookies UK', 'Chick-fil-A Singapore', 'Chick-fil-A UK'];
    return allowedFranchises.includes(selectedStore?.franchiseName || '') &&
      (hasPermissionTo(['RecipeAdmin']));
  };

  const renderBulkManageOption = () => {
    if (!shouldShowBulkManage()) {
      return null;
    }

    return (
      <Typography
        variant="textMD"
        display="flex"
        flexDirection="row"
        alignItems="center"
        sx={{
          p: 5,
          cursor: 'pointer',
          pointerEvents: isExporting ? 'none' : '',
        }}
        onClick={handleOpenBulk}
      >
        {t('bulkManage')}
      </Typography>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleExportRecipes = (): void => {
    if (selectedStore?.storeNumber) {
      setIsExporting(true);
      exportRecipes(selectedStore?.storeNumber)
        .then((response) => {
          if (response) {
            exportFile(
              response.data,
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              `Recipes-${selectedStore?.storeNumber}.xlsx`,
            );
          }
        })
        .catch((error) => {
          logError(error);
        })
        .finally(() => {
          setIsExporting(false);
          handleClose();
        });
    }
  };

  if (totalCount == 0 && !isLoading) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('recipes')}
      searchTerm={''}
      onClear={onClear}
      onAdd={onCreateNew}
      onAddIsCreating={false}
      addItemText={t('createNew')}
      onAddIsDisabled={false}
      withPaginationShown={false} />;
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="textMD"
            fontWeight={600}>
            {t('recipes')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('recipes')}`} />}
        </Box>

        {/* Right: Icon Button with Popover */}
        <Box sx={{ height: '24px' }}>
          <IconButton
            size="small"
            sx={{ height: 24, width: 24, p: 0 }}
            onClick={handleOpen}
          >
            <DotsVertical />
          </IconButton>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Typography
              variant="textMD"
              display="flex"
              alignItems="center"
              sx={{
                p: 5,
                cursor: 'pointer',
                pointerEvents: isExporting ? 'none' : '',
              }}
              onClick={handleExportRecipes}
            >
              {t('exportRecipes')}
              {isExporting && <Loader />}
            </Typography>
            {renderBulkManageOption()}
          </Popover>
        </Box>
      </Box >
      {
        isLoading ? <TableLoading headerHeight={45}
          bodyRowHeight={40}
          minTableWidth={1200} /> :
          <TableContainer>
            <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', minWidth: '1200px' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t('productSKU')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {t('productName')}
                  </TableCell>
                  <TableCell align='center'
                    sx={{ whiteSpace: 'nowrap' }}>
                    {t('status')}
                  </TableCell>
                  <TableCell align='center'
                    sx={{ whiteSpace: 'nowrap' }}>
                    {t('costPrice')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row) => (
                  <RecipeItemRow key={row.id}
                    item={row}
                    currencyCode={currencyCode}
                  />
                ))}
                <EmptyRows numberOfCurrentRows={list.length}
                  height={40} />
              </TableBody>
            </Table>
          </TableContainer>
      }
    </>
  );
};

export default RecipeList;